import { LockKeyhole, Radio } from "lucide-react";
import { useId, useRef, useState } from "react";

import { api, type Racecard } from "@/api";
import { useBetSlip } from "@/betslip/BetSlipProvider";
import { useCapabilities } from "@/capabilities/CapabilityProvider";
import { Button } from "@/components/ui";
import { trackTitle } from "@/features/racing/venue";
import { useSession } from "@/session/SessionProvider";

import { MarketBet } from "./MarketBet";
import { MarketForm } from "./MarketForm";
import { MarketMyBets } from "./MarketMyBets";

export type StraightBet = "win" | "place" | "show";
export type ExoticBet = "exacta" | "quinella" | "trifecta";
export type MarketPanelTab = "bet" | "form" | "bets";
type Market = Awaited<ReturnType<typeof api.markets>>;

export type HomeBettingCardProps = {
  card: Racecard | null;
  market: Market | undefined;
  nowMs: number;
  watchingRaceId: string | null;
  error?: unknown;
  empty?: boolean;
  onRetry: () => void;
  onBet: (gate: number, type: StraightBet, launcher?: HTMLElement) => void;
  tab?: MarketPanelTab;
  onTabChange?: (tab: MarketPanelTab) => void;
  pinned?: boolean;
  onAuto?: () => void;
  onAddExotic: (
    type: ExoticBet,
    selection: number[],
    oddsH: number,
    launcher?: HTMLElement,
  ) => void;
};

const TABS = [
  ["bet", "Bet"],
  ["form", "Form"],
  ["bets", "My Bets"],
] as const;

export function HomeBettingCard({
  card,
  market,
  nowMs,
  watchingRaceId,
  error,
  empty = false,
  onRetry,
  onBet,
  tab,
  onTabChange,
  pinned = false,
  onAuto,
  onAddExotic,
}: HomeBettingCardProps) {
  const [localTab, setLocalTab] = useState<MarketPanelTab>("bet");
  const activeTab = tab ?? localTab;
  const id = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { session } = useSession();
  const capabilities = useCapabilities();
  const betslip = useBetSlip();
  // Card, prices and the authoritative deadline always describe one market,
  // independently of whichever live race or replay the viewer is watching.
  const matched = Boolean(card && market?.raceId === card.raceId);
  const remaining = matched ? Date.parse(market!.locksAt) - nowMs : 0;
  const open = Boolean(
    matched &&
    market?.bettingOpen &&
    Number.isFinite(remaining) &&
    remaining > 0 &&
    !error &&
    !empty,
  );
  const restricted = Boolean(session && !capabilities.can("platform_betting"));
  const differentRace = Boolean(
    card && watchingRaceId && card.raceId !== watchingRaceId,
  );
  const countdown = Number.isFinite(remaining)
    ? Math.max(0, Math.ceil(remaining / 1000))
    : 0;
  const label = open
    ? `Closes in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
    : "Betting closed";
  const showSkeleton = !empty && !error && (!card || !matched);
  const raceNumber =
    typeof card?.chainIndex === "number" ? `Race ${card.chainIndex}` : "Race";
  const startsAt = card
    ? new Date(card.scheduledAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  function selectTab(next: MarketPanelTab) {
    setLocalTab(next);
    onTabChange?.(next);
  }

  return (
    <aside
      className="market-panel"
      aria-label="Race betting"
      data-betting-race-id={matched ? card?.raceId : undefined}
    >
      <header className="market-panel__header">
        <div className="market-panel__eyebrow">
          <span>
            {differentRace ? "Markets for another race" : "Make your pick"}
          </span>
          {open ? (
            <Radio size={14} aria-hidden />
          ) : (
            <LockKeyhole size={14} aria-hidden />
          )}
        </div>
        <div className="market-panel__mode">
          <span>{pinned ? "Selected" : "Auto"}</span>
          {pinned && onAuto ? (
            <button type="button" onClick={onAuto}>
              Return to auto
            </button>
          ) : null}
        </div>
        <h2>{card ? trackTitle(card) : "Race market"}</h2>
        <div className="market-panel__race">
          <span>
            {card
              ? `${raceNumber} · ${card.distanceM}m · ${startsAt}`
              : "Loading race details"}
          </span>
          <span
            className={
              open ? "market-panel__deadline is-open" : "market-panel__deadline"
            }
          >
            {matched ? label : "Waiting for market"}
          </span>
        </div>
        {differentRace ? (
          <p className="market-panel__note">
            These selections are for the market above, not the race on screen.
          </p>
        ) : null}
      </header>

      <div
        className="market-panel__tabs"
        role="tablist"
        aria-label="Race market sections"
      >
        {TABS.map(([value, title], index) => (
          <button
            type="button"
            key={value}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            role="tab"
            id={`${id}-tab-${value}`}
            aria-controls={`${id}-panel-${value}`}
            aria-selected={activeTab === value}
            tabIndex={activeTab === value ? 0 : -1}
            onClick={() => selectTab(value)}
            onKeyDown={(event) => {
              const next =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? TABS.length - 1
                    : event.key === "ArrowRight"
                      ? (index + 1) % TABS.length
                      : event.key === "ArrowLeft"
                        ? (index + TABS.length - 1) % TABS.length
                        : null;
              if (next === null) return;
              event.preventDefault();
              selectTab(TABS[next][0]);
              tabRefs.current[next]?.focus();
            }}
          >
            {title}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`${id}-panel-bet`}
        aria-labelledby={`${id}-tab-bet`}
        hidden={activeTab !== "bet"}
      >
        {empty ? (
          <div className="market-panel__state" role="status">
            <strong>No open race market</strong>
            <p>The next published race will appear here automatically.</p>
          </div>
        ) : error ? (
          <div className="market-panel__state" role="alert">
            <strong>Market temporarily unavailable</strong>
            <p>You can keep watching. Retry to refresh the betting window.</p>
            <Button variant="secondary" onClick={onRetry}>
              Retry market
            </Button>
          </div>
        ) : showSkeleton ? (
          <div
            className="market-panel__runners"
            aria-label="Loading runners"
            aria-busy="true"
          >
            {Array.from({ length: 8 }, (_, index) => (
              <div className="market-panel__placeholder" key={index}>
                <i />
                <span />
                <b />
              </div>
            ))}
          </div>
        ) : (
          <MarketBet
            key={card!.raceId}
            card={card!}
            market={market!}
            open={open && !restricted}
            active={activeTab === "bet"}
            onBet={onBet}
            onAddExotic={onAddExotic}
          />
        )}
        <footer className="market-panel__footer">
          <p>
            {restricted
              ? capabilities.reason("platform_betting")
              : session
                ? "Choose a horse, then set your stake in the betslip."
                : "Watch freely. Sign in to place your bet."}
          </p>
          <Button
            variant="secondary"
            onClick={(event) => betslip.setOpen(true, event.currentTarget)}
          >
            Multi / System · Open betslip
          </Button>
        </footer>
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel-form`}
        aria-labelledby={`${id}-tab-form`}
        hidden={activeTab !== "form"}
      >
        {activeTab === "form" ? (
          <MarketForm key={card?.raceId ?? "empty"} card={card} />
        ) : null}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel-bets`}
        aria-labelledby={`${id}-tab-bets`}
        hidden={activeTab !== "bets"}
      >
        {activeTab === "bets" ? <MarketMyBets /> : null}
      </div>
    </aside>
  );
}
